/**
 * Module dependencies.
 */

import {
  ReactCompareSlider,
  ReactCompareSliderImage
} from 'react-compare-slider';

import { Svg } from 'src/components/core/svg';
import { Text } from 'src/components/core/text';
import { media } from 'src/styles/utils/media';
import { useTranslation } from 'next-i18next';
import dragSvg from 'src/assets/svg/drag.svg';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  images: [string, string];
};

/**
 * `Slider` styled component.
 */

const Slider = styled(ReactCompareSlider)`
  height: min(calc(0.55 * var(--container-max-width)), 70vw);
  width: 100%;
`;

/**
 * `HandleSeparator` styled component.
 */

const HandleSeparator = styled.div`
  background-color: var(--color-gray200);
  color: var(--color-gray700);
  height: 100%;
  width: 3px;

  ${media.min.md`
    width: 4px;
  `}
`;

/**
 * `RoundButton` styled component.
 */

const RoundButton = styled.button`
  align-items: center;
  appearance: none;
  background-color: var(--color-white);
  border: none;
  border-radius: 50%;
  display: flex;
  height: 48px;
  justify-content: center;
  left: 50%;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 48px;

  ${media.min.ms`
    height: 88px;
    width: 88px;
  `}
`;

/**
 * `Slide` styled component.
 */

const Slide = styled.div`
  height: 100%;
  width: 100%;
`;

/**
 * `Label` styled component.
 */

const Label = styled(Text)`
  color: var(--color-white);
  position: absolute;
  top: 50%;
`;

/**
 * Handle.
 */

function Handle() {
  return (
    <HandleSeparator>
      <RoundButton>
        <Svg
          icon={dragSvg}
          size={'48px'}
        />
      </RoundButton>
    </HandleSeparator>
  );
}

/**
 * Handle.
 */

function ImageSlide({
  image,
  title
}: {
  image: string;
  title: 'after' | 'before';
}) {
  const { t } = useTranslation('common');

  return (
    <Slide>
      <ReactCompareSliderImage src={image} />

      <Label
        style={{ [title === 'after' ? 'right' : 'left']: 'min(80px, 6.5%)' }}
      >
        {t(`common:terms.${title}`)}
      </Label>
    </Slide>
  );
}

/**
 * Export `BeforeAfterSlider` component.
 */

export function BeforeAfterSlider(props: Props) {
  const { images } = props;

  return (
    <Slider
      boundsPadding={0}
      changePositionOnHover
      handle={<Handle />}
      itemOne={(
        <ImageSlide
          image={images[0]}
          title={'before'}
        />
      )}
      itemTwo={(
        <ImageSlide
          image={images[1]}
          title={'after'}
        />
      )}
      position={50}
    />
  );
}
